import { useEffect, useId, useRef, useState } from "react";
import { TextField } from "../../components/TextField/TextField";
import { Textarea } from "../../components/TextField/Textarea";
import { Button } from "../../components/Button/Button";
import { InlineNotice } from "../../components/InlineNotice/InlineNotice";
import { TenantMark } from "../../components/TenantMark/TenantMark";
import { SignupScene } from "../../components/SignupScene/SignupScene";
import { IconCheck, IconImage, IconTrash, IconUpload } from "../../components/icons/icons";
import { isValidHexColor, readableTextColor, sanitizeTenantColor } from "../../lib/colorUtils";
import { requestOwnerLogoUpload, updateOwnerBranding, uploadOwnerLogo } from "../../lib/api/owner";
import { ApiError } from "../../lib/apiClient";
import type { SignupTheme, Tenant } from "../../types";
import styles from "./BrandSettings.module.css";

const MAX_LOGO_BYTES = 2_000_000;
const ALLOWED_LOGO_TYPES: Record<string, true> = { "image/png": true, "image/jpeg": true, "image/webp": true };

interface BrandSettingsProps {
  tenant: Tenant;
  token: string;
  /** Owners can edit their own businesses; administrators see the same panel read-only. */
  canEdit: boolean;
  onUnauthorized: () => void;
  /** Called with the fresh Tenant after a successful save, so the caller can replace it in top-level state. */
  onTenantUpdated?: (tenant: Tenant) => void;
}

interface FormState {
  signupHeadline: string;
  welcomeMessage: string;
  primaryColor: string;
  signupTheme: SignupTheme;
}

interface FieldErrors {
  signupHeadline?: string;
  welcomeMessage?: string;
  primaryColor?: string;
  logo?: string;
}

interface SceneOption {
  value: SignupTheme;
  name: string;
  description: string;
}

const SCENE_OPTIONS: readonly SceneOption[] = [
  {
    value: "counter",
    name: "Counter glow",
    description: "Warm overhead light and a lit checkout edge, like signing up at the register.",
  },
  {
    value: "awning",
    name: "Market awning",
    description: "A striped canvas awning shades the page, with daylight drifting softly beneath it.",
  },
  {
    value: "window",
    name: "Window light",
    description: "Morning sun angles through the shopfront glass, crossed by soft pane shadows.",
  },
  {
    value: "festival",
    name: "Festival lights",
    description: "A string of warm bulbs twinkles overhead for a celebratory storefront.",
  },
];

const SCENE_LOOKUP: Record<SignupTheme, SceneOption> = Object.fromEntries(
  SCENE_OPTIONS.map((option) => [option.value, option]),
) as Record<SignupTheme, SceneOption>;

function formFromTenant(tenant: Tenant): FormState {
  return {
    signupHeadline: tenant.signupHeadline,
    welcomeMessage: tenant.welcomeMessage,
    primaryColor: tenant.primaryColor,
    signupTheme: tenant.signupTheme,
  };
}

export function BrandSettings({ tenant, token, canEdit, onUnauthorized, onTenantUpdated }: BrandSettingsProps) {
  const [form, setForm] = useState<FormState>(() => formFromTenant(tenant));
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoObjectUrl, setLogoObjectUrl] = useState<string | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const colorInputId = useId();

  // Switching businesses resets this editor. Brand saves update the same tenant prop, but the
  // editor has already adopted those values and keeps its success confirmation visible.
  useEffect(() => {
    setForm(formFromTenant(tenant));
    setLogoFile(null);
    setLogoRemoved(false);
    setErrors({});
    setStatus("idle");
    setStatusMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.tenantId]);

  useEffect(() => {
    if (!logoFile) {
      setLogoObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const isDirty =
    form.signupHeadline !== tenant.signupHeadline ||
    form.welcomeMessage !== tenant.welcomeMessage ||
    form.primaryColor !== tenant.primaryColor ||
    form.signupTheme !== tenant.signupTheme ||
    logoFile !== null ||
    logoRemoved;

  const previewLogoUrl = logoRemoved ? undefined : (logoObjectUrl ?? tenant.logoUrl);
  const safeColor = sanitizeTenantColor(form.primaryColor);
  const previewInk = readableTextColor(safeColor, "#201c17", "#ffffff");

  function handleLogoChoose(file: File | undefined) {
    if (!file) return;
    if (!Object.hasOwn(ALLOWED_LOGO_TYPES, file.type)) {
      setErrors((prev) => ({ ...prev, logo: "Use a PNG, JPEG, or WebP image." }));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setErrors((prev) => ({ ...prev, logo: "Logo must be 2 MB or smaller." }));
      return;
    }
    setErrors((prev) => ({ ...prev, logo: undefined }));
    setLogoRemoved(false);
    setLogoFile(file);
  }

  function handleRemoveLogo() {
    setLogoFile(null);
    setLogoRemoved(true);
    setErrors((prev) => ({ ...prev, logo: undefined }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleDiscard() {
    setForm(formFromTenant(tenant));
    setLogoFile(null);
    setLogoRemoved(false);
    setErrors({});
    setStatus("idle");
    setStatusMessage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    const headline = form.signupHeadline.trim();
    const welcome = form.welcomeMessage.trim();
    if (headline.length < 2 || headline.length > 70) {
      next.signupHeadline = "Use 2–70 characters.";
    }
    if (welcome.length < 2 || welcome.length > 180) {
      next.welcomeMessage = "Use 2–180 characters.";
    }
    if (!isValidHexColor(form.primaryColor)) {
      next.primaryColor = "Enter a valid color, like #D95032.";
    }
    return next;
  }

  async function handleSave() {
    const fieldErrors = validate();
    setErrors((prev) => ({ ...fieldErrors, logo: prev.logo }));
    if (Object.keys(fieldErrors).length > 0) return;

    setStatus("saving");
    setStatusMessage(null);
    try {
      let logoKey: string | null;
      if (logoFile) {
        const upload = await requestOwnerLogoUpload(token, tenant.tenantId, logoFile.type);
        await uploadOwnerLogo(upload, logoFile);
        logoKey = upload.logoKey;
      } else if (logoRemoved) {
        logoKey = null;
      } else {
        logoKey = tenant.logoUrl ? tenant.logoUrl.replace(/^\//, "") : null;
      }

      const { tenant: updated } = await updateOwnerBranding(token, tenant.tenantId, {
        primaryColor: form.primaryColor.trim(),
        signupHeadline: form.signupHeadline.trim(),
        welcomeMessage: form.welcomeMessage.trim(),
        signupTheme: form.signupTheme,
        logoKey,
      });

      setLogoFile(null);
      setLogoRemoved(false);
      setStatus("success");
      setStatusMessage("Brand kit saved.");
      onTenantUpdated?.(updated);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setStatus("error");
      setStatusMessage(err instanceof Error ? err.message : "Couldn't save the brand kit. Please try again.");
    }
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div>
          <h2 className={styles.heading}>Brand kit</h2>
          <p className={styles.subheading}>
            {canEdit
              ? "Controls the logo, accent color, and copy shoppers see on the signup page."
              : "Read-only. The assigned owner manages this business's brand kit."}
          </p>
        </div>
        {canEdit && isDirty && status !== "saving" && <span className={styles.unsaved}>Unsaved changes</span>}
      </div>

      <div className={styles.grid}>
        <div className={styles.form}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Logo</span>
            <div className={styles.logoRow}>
              <div className={styles.logoPreview}>
                {previewLogoUrl ? (
                  <img src={previewLogoUrl} alt="" className={styles.logoImage} />
                ) : (
                  <IconImage className={styles.logoPlaceholder} width={22} height={22} />
                )}
              </div>
              {canEdit && (
                <div className={styles.logoActions}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className={styles.fileInput}
                    id="brand-logo-input"
                    onChange={(e) => handleLogoChoose(e.target.files?.[0])}
                  />
                  <label htmlFor="brand-logo-input" className={styles.uploadButton}>
                    <IconUpload width={15} height={15} />
                    {previewLogoUrl ? "Replace logo" : "Upload logo"}
                  </label>
                  {previewLogoUrl && (
                    <Button type="button" variant="danger" onClick={handleRemoveLogo}>
                      <IconTrash width={15} height={15} />
                      Remove
                    </Button>
                  )}
                </div>
              )}
            </div>
            <span className={styles.hint}>PNG, JPEG, or WebP. Up to 2 MB.</span>
            {errors.logo && (
              <span className={styles.fieldError} role="alert">
                {errors.logo}
              </span>
            )}
          </div>

          {canEdit ? (
            <div className={styles.field}>
              <div className={styles.colorRow}>
                <div className={styles.colorPickerField}>
                  <label htmlFor={colorInputId} className={styles.colorPickerLabel}>
                    Accent color
                  </label>
                  <input
                    type="color"
                    id={colorInputId}
                    className={styles.colorSwatch}
                    value={safeColor}
                    onChange={(e) => setForm((prev) => ({ ...prev, primaryColor: e.target.value }))}
                  />
                </div>
                <TextField
                  label="Hex value"
                  value={form.primaryColor}
                  onChange={(e) => setForm((prev) => ({ ...prev, primaryColor: e.target.value }))}
                  error={errors.primaryColor}
                  className={styles.colorHexField}
                />
              </div>
            </div>
          ) : (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Accent color</span>
              <div className={styles.readRow}>
                <span className={styles.readSwatch} style={{ background: safeColor }} aria-hidden="true" />
                <span className={styles.readValue}>{form.primaryColor}</span>
              </div>
            </div>
          )}

          {canEdit ? (
            <TextField
              label="Signup headline"
              value={form.signupHeadline}
              onChange={(e) => setForm((prev) => ({ ...prev, signupHeadline: e.target.value }))}
              error={errors.signupHeadline}
              hint={`${form.signupHeadline.trim().length}/70 characters`}
            />
          ) : (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Signup headline</span>
              <p className={styles.readValue}>{form.signupHeadline}</p>
            </div>
          )}

          {canEdit ? (
            <Textarea
              label="Welcome message"
              rows={3}
              value={form.welcomeMessage}
              onChange={(e) => setForm((prev) => ({ ...prev, welcomeMessage: e.target.value }))}
              error={errors.welcomeMessage}
              hint={`${form.welcomeMessage.trim().length}/180 characters`}
            />
          ) : (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Welcome message</span>
              <p className={styles.readValue}>{form.welcomeMessage}</p>
            </div>
          )}

          {canEdit ? (
            <fieldset className={styles.sceneField}>
              <legend className={styles.fieldLabel}>Signup scene</legend>
              <div className={styles.sceneGrid}>
                {SCENE_OPTIONS.map((option) => {
                  const selected = form.signupTheme === option.value;
                  return (
                    <label
                      key={option.value}
                      className={selected ? `${styles.sceneOption} ${styles.sceneOptionSelected}` : styles.sceneOption}
                    >
                      <input
                        type="radio"
                        name="signupTheme"
                        value={option.value}
                        checked={selected}
                        onChange={() => setForm((prev) => ({ ...prev, signupTheme: option.value }))}
                        className={styles.sceneRadioInput}
                      />
                      <span className={styles.sceneThumb}>
                        <SignupScene theme={option.value} variant="swatch" />
                      </span>
                      <span className={styles.sceneText}>
                        <span className={styles.sceneName}>
                          {option.name}
                          {selected && <IconCheck className={styles.sceneCheck} width={13} height={13} />}
                        </span>
                        <span className={styles.sceneDescription}>{option.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ) : (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Signup scene</span>
              <div className={styles.sceneReadRow}>
                <span className={styles.sceneThumb}>
                  <SignupScene theme={tenant.signupTheme} variant="swatch" />
                </span>
                <span className={styles.readValue}>
                  {SCENE_LOOKUP[tenant.signupTheme].name} — {SCENE_LOOKUP[tenant.signupTheme].description}
                </span>
              </div>
            </div>
          )}

          {status === "error" && statusMessage && <InlineNotice tone="error">{statusMessage}</InlineNotice>}
          {status === "success" && statusMessage && <InlineNotice tone="success">{statusMessage}</InlineNotice>}

          {canEdit && (
            <div className={styles.actions}>
              {isDirty && (
                <Button type="button" variant="ghost" onClick={handleDiscard} disabled={status === "saving"}>
                  Discard changes
                </Button>
              )}
              <Button type="button" onClick={() => void handleSave()} busy={status === "saving"} disabled={!isDirty}>
                {status === "saving" ? "Saving…" : "Save brand kit"}
              </Button>
            </div>
          )}
        </div>

        <div className={styles.previewWrap}>
          <span className={styles.previewLabel}>Signup page preview</span>
          <div className={styles.previewCard}>
            <SignupScene theme={form.signupTheme} variant="card" />
            <div className={styles.previewContent}>
              <TenantMark name={tenant.name} color={form.primaryColor} logoUrl={previewLogoUrl} size="sm" />
              <p className={styles.previewHeadline}>{form.signupHeadline || "Signup headline"}</p>
              <p className={styles.previewWelcome}>{form.welcomeMessage || "Welcome message"}</p>
              <span
                className={styles.previewButton}
                style={{
                  background: safeColor,
                  color: previewInk,
                }}
              >
                Sign up
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
